<template>
  <div
    class="d-flex flex-column gap-2 flex-wrap p-2 bg-white rounded"
    :class="{
      'visitor-loading': state.isLoading
    }"
  >
    <ErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />

    <div class="d-flex align-items-center justify-content-between">
      <h4 class="m-0 p-2">Tardy Upload Settings</h4>
      <ActionPanel
        @tab-clicked="handleTardyFileUploadMode"
        :selected-tab-position="selectedTabPosition"
        :tabs="fileUploadModes"
      />
    </div>

    <InfoBox class="flex-grow-1">
      <p class="text-muted mb-2">
        Choose how tardy data files (LTS/LTC) will be uploaded to the system:
      </p>
      <ul class="text-muted small">
        <li><strong>Disabled:</strong> No file uploads allowed</li>
        <li>
          <strong>SFTP Upload:</strong> Files are uploaded automatically via an
          SFTP server
        </li>
        <li>
          <strong>Manual Upload:</strong> Files are uploaded manually through
          the web interface
        </li>
      </ul>
    </InfoBox>

    <InfoBox v-if="requiresFileUpload">
      <div class="d-flex gap-3 align-items-center">
        <span class="text-muted fw-bold">Download sample files:</span>
        <div
          class="v3-file-upload-download fw-bold"
          @click="downloadCSVSample('/csv-samples/lts-file-upload-sample.csv')"
        >
          <i class="ri-download-2-line download-icon" />
          LTS Sample
        </div>

        <div
          class="v3-file-upload-download fw-bold"
          @click="downloadCSVSample('/csv-samples/ltc-file-upload-sample.csv')"
        >
          <i class="ri-download-2-line download-icon" />
          LTC Sample
        </div>
      </div>
    </InfoBox>
  </div>
</template>
<script>
import passConstants from "@/constants/passConstants"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useToast } from "vue-toastification"
import { useStore } from "vuex"

export default {
  name: "TardyUploadSetting",
  components: {
    ActionPanel,
    ErrorHandler,
    InfoBox
  },
  setup() {
    const store = useStore()
    const toast = useToast()
    const state = reactive({
      isLoading: false,
      fileUploadMode: null
    })
    const serverError = ref(null)
    const fileUploadModes = [
      {
        label: "Disabled",
        value: "off"
      },
      {
        label: "SFTP Upload",
        value: "sftp"
      },
      {
        label: "Manual Upload",
        value: "manual"
      }
    ]

    const currentFileUploadMode = computed(() => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      if (tardyModule?.pivot?.option_json) {
        const options = JSON.parse(tardyModule.pivot.option_json)
        return (
          options["file-upload-mode"] ||
          passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
        )
      }
      return passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
    })

    const selectedTabPosition = computed(() => {
      const mode = currentFileUploadMode.value
      const index = fileUploadModes.findIndex((tab) => tab.value === mode)
      return index >= 0 ? index : 0
    })

    const requiresFileUpload = computed(() => {
      return (
        state?.fileUploadMode === "sftp" || state?.fileUploadMode === "manual"
      )
    })

    const handleTardyFileUploadMode = async (tab) => {
      if (!tab?.value || tab.value === state.fileUploadMode) return

      try {
        state.isLoading = true
        serverError.value = null
        await updateFileUploadMode(tab.value)
        state.fileUploadMode = tab.value
      } catch (error) {
        serverError.value = error?.response?.data?.message || error?.message
        toast.error(
          "Something went wrong while we were updating the file upload mode"
        )
      } finally {
        state.isLoading = false
      }
    }

    const updateFileUploadMode = async (newMode) => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      if (!tardyModule?.pivot?.option_json) {
        throw new Error("Tardy module not found or not configured")
      }
      const currentOptions = JSON.parse(tardyModule.pivot.option_json)
      const updatedOptions = {
        ...currentOptions,
        "file-upload-mode": newMode
      }

      await store.dispatch("modules/updateTardyModule", {
        option_json: JSON.stringify(updatedOptions)
      })

      await store.dispatch("schools/getActiveModules", {
        clearCacheEntry: true
      })
    }

    const downloadCSVSample = (path) => {
      const fileName = path?.split?.("/")?.[2] || "sample.csv"
      const link = document.createElement("a")
      link.href = path
      link.setAttribute("download", fileName)
      link.click()
    }

    onMounted(() => {
      state.fileUploadMode = currentFileUploadMode.value
    })

    watch(
      () => currentFileUploadMode.value,
      (newMode) => {
        if (newMode !== state.fileUploadMode) {
          state.fileUploadMode = newMode
        }
      }
    )

    return {
      state,
      fileUploadModes,
      serverError,
      selectedTabPosition,
      handleTardyFileUploadMode,
      downloadCSVSample,
      requiresFileUpload
    }
  }
}
</script>
